import React, { useEffect, useState, useCallback } from "react";
import { toast } from "react-toastify";
import { Loader2, Plus, Check, Trash2, Phone, MapPin, Bell, CheckSquare } from "lucide-react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../ui/select";

import api from "../../services/api";
import toastError from "../../errors/toastError";

export const TIPOS_DE_ATIVIDADE = [
	{ value: "call", rotulo: "Ligar", icone: Phone },
	{ value: "visit", rotulo: "Visitar", icone: MapPin },
	{ value: "followup", rotulo: "Cobrar resposta", icone: Bell },
	{ value: "task", rotulo: "Tarefa", icone: CheckSquare },
];

const iconeDoTipo = (tipo) =>
	TIPOS_DE_ATIVIDADE.find((t) => t.value === tipo)?.icone || CheckSquare;

// "Ligar para o cliente na terça" — o compromisso que antes só existia na
// cabeça de quem vende. Vale para contato e para cartão do Kanban: o bloco é
// o mesmo, muda a quem a tarefa se amarra.
const Atividades = ({ contactId, cardId }) => {
	const [atividades, setAtividades] = useState([]);
	const [carregando, setCarregando] = useState(false);
	const [ocupado, setOcupado] = useState(false);

	const [titulo, setTitulo] = useState("");
	const [tipo, setTipo] = useState("call");
	const [quando, setQuando] = useState("");

	const carregar = useCallback(async () => {
		if (!contactId && !cardId) return;

		setCarregando(true);
		try {
			const { data } = await api.get("/activities", {
				params: {
					...(contactId ? { contactId } : {}),
					...(cardId ? { cardId } : {}),
					escopo: "todas",
				},
			});
			setAtividades(data.activities || []);
		} catch (err) {
			toastError(err);
		}
		setCarregando(false);
	}, [contactId, cardId]);

	useEffect(() => {
		carregar();
	}, [carregar]);

	const criar = async () => {
		if (!titulo.trim() || !quando) {
			toast.error("Diga o que fazer e quando.");
			return;
		}

		setOcupado(true);
		try {
			await api.post("/activities", {
				title: titulo,
				type: tipo,
				// datetime-local devolve "AAAA-MM-DDTHH:mm" sem fuso; o servidor lê
				// como hora local, que é o que a pessoa digitou.
				dueAt: quando,
				contactId: contactId || null,
				cardId: cardId || null,
			});
			setTitulo("");
			setQuando("");
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	const concluir = async (atividade) => {
		setOcupado(true);
		try {
			await api.put(`/activities/${atividade.id}`, {
				done: !atividade.doneAt,
			});
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	const apagar = async (atividade) => {
		setOcupado(true);
		try {
			await api.delete(`/activities/${atividade.id}`);
			await carregar();
		} catch (err) {
			toastError(err);
		}
		setOcupado(false);
	};

	const agora = Date.now();

	return (
		<div className="space-y-2">
			{carregando ? (
				<div className="flex justify-center py-4">
					<Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
				</div>
			) : (
				atividades.map((atividade) => {
					const Icone = iconeDoTipo(atividade.type);
					const atrasada =
						!atividade.doneAt && new Date(atividade.dueAt).getTime() < agora;

					return (
						<div
							key={atividade.id}
							className="flex items-center gap-2 rounded-lg border p-2"
						>
							<Icone
								className={`h-4 w-4 shrink-0 ${
									atrasada ? "text-destructive" : "text-muted-foreground"
								}`}
							/>
							<div className="min-w-0 flex-1">
								<p
									className={`truncate text-sm ${
										atividade.doneAt ? "text-muted-foreground line-through" : ""
									}`}
								>
									{atividade.title}
								</p>
								<p
									className={`text-xs ${
										atrasada ? "text-destructive" : "text-muted-foreground"
									}`}
								>
									{new Date(atividade.dueAt).toLocaleString("pt-BR", {
										day: "2-digit",
										month: "2-digit",
										hour: "2-digit",
										minute: "2-digit",
									})}
									{atividade.user ? ` · ${atividade.user.name}` : ""}
									{atrasada ? " · atrasada" : ""}
								</p>
							</div>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 shrink-0"
								aria-label={
									atividade.doneAt
										? `Reabrir ${atividade.title}`
										: `Concluir ${atividade.title}`
								}
								onClick={() => concluir(atividade)}
								disabled={ocupado}
							>
								<Check
									className={`h-4 w-4 ${
										atividade.doneAt ? "text-emerald-600" : ""
									}`}
								/>
							</Button>
							<Button
								variant="ghost"
								size="icon"
								className="h-8 w-8 shrink-0 text-destructive hover:text-destructive"
								aria-label={`Excluir ${atividade.title}`}
								onClick={() => apagar(atividade)}
								disabled={ocupado}
							>
								<Trash2 className="h-4 w-4" />
							</Button>
						</div>
					);
				})
			)}

			{!carregando && atividades.length === 0 && (
				<p className="py-2 text-center text-xs text-muted-foreground">
					Nada marcado. O que ficar aqui vira lembrete no chat interno de quem
					for responsável.
				</p>
			)}

			<div className="flex flex-wrap gap-2">
				<Input
					className="h-8 min-w-40 flex-1"
					value={titulo}
					onChange={(e) => setTitulo(e.target.value)}
					placeholder="Ex.: ligar sobre o orçamento"
				/>
				<Select value={tipo} onValueChange={setTipo}>
					<SelectTrigger className="h-8 w-36">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{TIPOS_DE_ATIVIDADE.map((t) => (
							<SelectItem key={t.value} value={t.value}>
								{t.rotulo}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Input
					className="h-8 w-48"
					type="datetime-local"
					value={quando}
					onChange={(e) => setQuando(e.target.value)}
					aria-label="Quando"
				/>
				<Button size="sm" onClick={criar} disabled={ocupado}>
					<Plus className="h-4 w-4" />
					Marcar
				</Button>
			</div>
		</div>
	);
};

export default Atividades;
